import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Calendar, MapPin, Clock, Phone, Mail, Globe, ArrowLeft, Share2, User, Eye } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import Layout from "@/components/layout/Layout";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";

const CATEGORIES = {
  ferias: { name: "Ferias", icon: "🎪", color: "#F59E0B" },
  conciertos: { name: "Conciertos", icon: "🎵", color: "#8B5CF6" },
  deportes: { name: "Deportes", icon: "⚽", color: "#10B981" },
  municipal: { name: "Municipal", icon: "🏛️", color: "#3B82F6" },
  religioso: { name: "Religioso", icon: "⛪", color: "#EC4899" },
  escolar: { name: "Escolar", icon: "🎓", color: "#06B6D4" },
  otro: { name: "Otro", icon: "📅", color: "#6B7280" },
};

const formatDate = (dateStr) => {
  if (!dateStr) return "";
  const date = new Date(dateStr + "T00:00:00");
  return date.toLocaleDateString("es-HN", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric"
  });
};

const formatTime = (timeStr) => {
  if (!timeStr) return "";
  const [hours, minutes] = timeStr.split(":");
  const h = parseInt(hours);
  const ampm = h >= 12 ? "PM" : "AM";
  const hour12 = h % 12 || 12;
  return `${hour12}:${minutes} ${ampm}`;
};

export default function EventoDetailPage() {
  const { slug } = useParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchEvent();
  }, [slug]);

  const fetchEvent = async () => {
    try {
      const response = await axios.get(`${API}/public/events/${slug}`);
      setEvent(response.data);
    } catch (error) {
      console.error("Error fetching event:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: event.title,
        text: event.description || `Evento en Choluteca: ${event.title}`,
        url: window.location.href
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      toast.success("Enlace copiado al portapapeles");
    }
  };

  const catInfo = event ? (CATEGORIES[event.category] || CATEGORIES.otro) : null;

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
        </div>
      </Layout>
    );
  }

  if (!event) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Evento no encontrado</h2>
            <Link to="/eventos" className="text-emerald-600 hover:underline">
              Ver todos los eventos
            </Link>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen bg-gray-50">
        {/* Back button */}
        <div className="bg-white border-b sticky top-0 z-10">
          <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
            <Link 
              to="/eventos" 
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
            >
              <ArrowLeft className="h-5 w-5" />
              <span>Eventos</span>
            </Link>
            <Button variant="ghost" size="icon" onClick={handleShare}>
              <Share2 className="h-5 w-5" />
            </Button>
          </div>
        </div>

        {/* Hero Image */}
        {event.image_url ? (
          <div className="h-64 md:h-80 bg-gray-200 relative">
            <img 
              src={event.image_url} 
              alt={event.title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
          </div>
        ) : (
          <div 
            className="h-48 md:h-64 flex items-center justify-center text-6xl"
            style={{ backgroundColor: catInfo.color + "20" }}
          >
            {catInfo.icon}
          </div>
        )}

        {/* Content */}
        <div className="max-w-4xl mx-auto px-4 -mt-8 relative z-10">
          <div className="bg-white rounded-xl shadow-lg p-6 md:p-8">
            {/* Category Badge */}
            <Badge 
              className="mb-4 text-white"
              style={{ backgroundColor: catInfo.color }}
            >
              {catInfo.icon} {catInfo.name}
            </Badge>

            {/* Title */}
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
              {event.title}
            </h1>

            {/* Date & Time */}
            <div className="bg-emerald-50 rounded-xl p-4 mb-6">
              <div className="flex flex-wrap gap-6">
                <div className="flex items-center gap-3">
                  <div className="bg-emerald-600 text-white p-3 rounded-xl">
                    <Calendar className="h-6 w-6" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Fecha</p>
                    <p className="font-semibold text-gray-900 capitalize">
                      {formatDate(event.start_date)}
                    </p>
                    {event.end_date && event.end_date !== event.start_date && (
                      <p className="text-sm text-gray-500 capitalize">
                        hasta {formatDate(event.end_date)}
                      </p>
                    )}
                  </div>
                </div>

                {event.start_time && (
                  <div className="flex items-center gap-3">
                    <div className="bg-emerald-600 text-white p-3 rounded-xl">
                      <Clock className="h-6 w-6" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Hora</p>
                      <p className="font-semibold text-gray-900">
                        {formatTime(event.start_time)}
                        {event.end_time && ` - ${formatTime(event.end_time)}`}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Location */}
            {(event.location || event.address) && (
              <div className="flex items-start gap-3 mb-6 p-4 bg-gray-50 rounded-xl">
                <MapPin className="h-5 w-5 text-emerald-600 mt-0.5" />
                <div>
                  <p className="font-medium text-gray-900">{event.location}</p>
                  {event.address && (
                    <p className="text-sm text-gray-500">{event.address}</p>
                  )}
                  {event.lat && event.lng && (
                    <a
                      href={`https://maps.google.com/?q=${event.lat},${event.lng}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-emerald-600 hover:underline mt-1 inline-block"
                    >
                      Ver en Google Maps →
                    </a>
                  )}
                </div>
              </div>
            )}

            {/* Description */}
            {event.description && (
              <div className="mb-6">
                <h2 className="font-semibold text-gray-900 mb-2">Descripción</h2>
                <p className="text-gray-600 whitespace-pre-line">{event.description}</p>
              </div>
            )}

            {/* Organizer & Contact */}
            {(event.organizer || event.contact_phone || event.contact_email) && (
              <div className="border-t pt-6 mt-6">
                <h2 className="font-semibold text-gray-900 mb-4">Contacto</h2>
                <div className="space-y-3">
                  {event.organizer && (
                    <div className="flex items-center gap-3 text-gray-600">
                      <User className="h-5 w-5" />
                      <span>{event.organizer}</span>
                    </div>
                  )}
                  {event.contact_phone && (
                    <a 
                      href={`tel:${event.contact_phone}`}
                      className="flex items-center gap-3 text-emerald-600 hover:underline"
                    >
                      <Phone className="h-5 w-5" />
                      <span>{event.contact_phone}</span>
                    </a>
                  )}
                  {event.contact_email && (
                    <a 
                      href={`mailto:${event.contact_email}`}
                      className="flex items-center gap-3 text-emerald-600 hover:underline"
                    >
                      <Mail className="h-5 w-5" />
                      <span>{event.contact_email}</span>
                    </a>
                  )}
                  {event.website_url && (
                    <a 
                      href={event.website_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 text-emerald-600 hover:underline"
                    >
                      <Globe className="h-5 w-5" />
                      <span>Sitio web</span>
                    </a>
                  )}
                </div>
              </div>
            )}

            {/* Views */}
            <div className="flex items-center gap-2 text-sm text-gray-400 mt-6 pt-6 border-t">
              <Eye className="h-4 w-4" />
              <span>{event.view_count || 0} vistas</span>
            </div>
          </div>
        </div>

        {/* Spacer */}
        <div className="h-12" />
      </div>
    </Layout>
  );
}
